<template>
  <Card :title="$t('global.php')" logo="php">
    <CardActionContent :buttons="getButtons()">
      <template #description>
        <CardListContent :list="getList()" />
        <Alert
          v-if="isUpdating()"
          :message="getUpdatingMsg()"
          variant="info"
          :dismissible="false"
        />
        <Alert
          v-if="hasCompatibilityError()"
          :message="getIncompatibilityMsg()"
          variant="warning"
          :dismissible="false"
        />
      </template>
    </CardActionContent>
  </Card>
</template>

<script>
import { computed, onMounted, ref } from "@vue/composition-api";
import Alert from "@/components/display/Alert";
import Card from "@/components/display/Card";
import CardActionContent from "@/components/display/card-sub-components/CardActionContent";
import CardListContent from "@/components/display/card-sub-components/CardListContent";
import NavLink from "@/classes/NavLink";
import PhpUpdate from "@/components/smart/modals/PhpUpdate";
import PhpUpdateConfirm from "@/components/smart/modals/PhpUpdateConfirm";

import store from "@/store";

export default {
  name: "Php",
  components: {
    Alert,
    Card,
    CardActionContent,
    CardListContent,
  },
  props: {
    resourceName: String,
    site: Object,
    siteStatus: {
      type: Object,
      required: true,
    },
  },
  setup(props, { root }) {
    const compatibilityCheck = computed(() => store.getters["php/getCompatibilityCheck"]);
    const status = computed(() => store.getters["php/getCompatibilityCheckStatus"]);
    const changeCancelled = ref(false);
    const versionUpdating = computed(() => store.getters["php/isVersionUpdating"](props.site.id));

    const cancelChange = () => (changeCancelled.value = true);

    const getButtons = () => {
      const disable =
        status.value.isUpdating || props.siteStatus.isUpdating || versionUpdating.value;
      if (status.value.isUpdating || status.value.isLoaded) {
        if (hasCompatibilityError()) {
          return [
            new NavLink(root.$t("global.cancel"), null, cancelChange, false, null, null, disable),
            new NavLink(
              root.$t("global.php-change-version-confirm", { version: getNewVersion() }),
              null,
              openPhpUpdateConfirm,
              false,
              "primary",
              null,
              disable
            ),
          ];
        }

        return [
          new NavLink(
            root.$t("global.php-change-version"),
            null,
            openPhpUpdate,
            false,
            "primary",
            null,
            disable
          ),
        ];
      }
      return [];
    };

    const getCompatibilityCheckStatus = () => compatibilityCheck.value?.status;

    const getCompatibilityCheckRunningMsg = () =>
      root.$t("global.compatibility-check-running", { version: getNewVersion() });

    const getIncompatibilityMsg = () =>
      root.$t("global.php-incompatibility", { version: getNewVersion() });

    const getList = () => [
      {
        label: root.$t("global.version"),
        content: props.site.environment.software.php.version,
        canCopy: true,
      },
      {
        label: root.$t("global.location"),
        content: props.site.environment.software.php.cli,
        canCopy: true,
      },
    ];

    const getNewVersion = () => compatibilityCheck.value?.phpVersion;

    const getType = () => props.site.app.type;

    const getUpdatingMsg = () =>
      isCompatibilityCheckRunning()
        ? getCompatibilityCheckRunningMsg()
        : root.$t("global.php-changing-version-message");

    const getUrl = () => props.site.environment.access.url;

    const hasCompatibilityError = () =>
      getCompatibilityCheckStatus() === "error" && !changeCancelled.value;

    const isCompatibilityCheckRunning = () =>
      ["pending", "processing"].includes(getCompatibilityCheckStatus());

    const isLoading = () => status.value.isLoading;
    const isErrored = () => status.value.isErrored;
    const isUpdating = () => versionUpdating.value || isCompatibilityCheckRunning();

    const openPhpUpdate = () => {
      store.dispatch("modals/open", {
        template: PhpUpdate,
        data: { resourceName: props.resourceName, site: props.site },
      });
    };

    const openPhpUpdateConfirm = () => {
      store.dispatch("modals/open", {
        template: PhpUpdateConfirm,
        data: {
          incompatible: hasCompatibilityError(),
          resourceName: props.resourceName,
          site: props.site,
          version: getNewVersion(),
        },
      });
    };

    onMounted(() => {
      store.dispatch("php/getCompatibilityCheck", props.site.id);
    });

    return {
      getButtons,
      getIncompatibilityMsg,
      getList,
      getType,
      getUpdatingMsg,
      getUrl,
      hasCompatibilityError,
      isCompatibilityCheckRunning,
      isLoading,
      isErrored,
      isUpdating,
    };
  },
};
</script>
